import React, { useEffect, useState, useCallback } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  ActivityIndicator,
  Alert,
} from 'react-native';
import { SafeAreaView, useSafeAreaInsets } from 'react-native-safe-area-context';
import { useKeyboardVisible } from '@/hooks/useKeyboardVisible';
import { router, useLocalSearchParams } from 'expo-router';
import { Svg, Path } from 'react-native-svg';
import { format, parseISO, isBefore, parse } from 'date-fns';
import { ptBR, enUS, es } from 'date-fns/locale';
import {
  getSponsorAvailability,
  AvailableSlot,
  SponsorAgendaSummary,
  groupSlotsByDate,
  rescheduleAppointment,
} from '../../../services/sponsorService';
import { useLanguage } from '@/contexts/LanguageContext';
import { profileService } from '@/services/profileService';
import PageHeader from '../../../components/PageHeader';
import { Skeleton } from '../../../components/Skeleton';
import Avatar from '../../../components/Avatar';
import ProfessionalDetails from '../../../components/ProfessionalDetails';

const AGENDA_VISIBLE_DETAIL_ROWS = ['crp'] as const;

const CalendarIcon = () => (
  <Svg width='24' height='24' viewBox='0 0 24 24' fill='none'>
    <Path
      d='M15.1523 3.69238V6.6677'
      stroke='#3D3C38'
      strokeWidth='2'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
    <Path
      d='M7.92969 3.69238V6.6677'
      stroke='#3D3C38'
      strokeWidth='2'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
    <Path
      fillRule='evenodd'
      clipRule='evenodd'
      d='M15.683 5.06445L7.40863 5.06527C4.97812 5.06527 3.46094 6.41874 3.46094 8.90651V16.3957C3.46094 18.9229 4.97812 20.3077 7.40863 20.3077L15.6759 20.3068C18.1136 20.3068 19.6235 18.9462 19.6235 16.4576V8.90569C19.6307 6.41792 18.1207 5.06445 15.683 5.06445Z'
      stroke='#3D3C38'
      strokeWidth='2'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
    <Path
      d='M7.52344 11.4375H7.53344'
      stroke='#3D3C38'
      strokeWidth='2'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
    <Path
      d='M7.52344 14.8916H7.53344'
      stroke='#3D3C38'
      strokeWidth='2'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
    <Path
      d='M11.5391 11.4375H11.5491'
      stroke='#3D3C38'
      strokeWidth='2'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
    <Path
      d='M11.5391 14.8916H11.5491'
      stroke='#3D3C38'
      strokeWidth='2'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
    <Path
      d='M15.5391 11.4375H15.5491'
      stroke='#3D3C38'
      strokeWidth='2'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
    <Path
      d='M15.5391 14.8916H15.5491'
      stroke='#3D3C38'
      strokeWidth='2'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
  </Svg>
);

export default function SponsorAgenda() {
  const { t, language } = useLanguage();
  const { id, isReschedule, appointmentId } = useLocalSearchParams<{
    id: string;
    isReschedule?: string;
    appointmentId?: string | string[];
  }>();
  const isRescheduling = isReschedule === '1';
  const resolvedAppointmentId = Array.isArray(appointmentId) ? appointmentId[0] : appointmentId;
  const insets = useSafeAreaInsets();
  const keyboardVisible = useKeyboardVisible();

  const [sponsor, setSponsor] = useState<SponsorAgendaSummary | null>(null);
  const [allSlots, setAllSlots] = useState<AvailableSlot[]>([]);
  const [groupedSlots, setGroupedSlots] = useState<Record<string, AvailableSlot[]>>({});
  const [selectedSlot, setSelectedSlot] = useState<{ date: string; time: string } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [userPhone, setUserPhone] = useState<string | null>(null);
  const [scheduling, setScheduling] = useState(false);
  const professionalDetailLabels = {
    crp: t('sponsors.details.crp'),
    session: t('sponsors.details.session'),
    price: t('sponsors.details.price'),
  };
  const locale = language === 'pt' ? 'pt-BR' : language === 'es' ? 'es-ES' : 'en-US';
  const formatSessionDuration = (minutes: number) =>
    t('sponsors.details.durationMinutes', { minutes: String(minutes) });

  const getLocale = () => {
    if (language.startsWith('pt')) return ptBR;
    if (language.startsWith('es')) return es;
    return enUS;
  };

  const isSlotInPast = useCallback((slotDate: string, slotTime: string): boolean => {
    try {
      const slotDateTime = parse(`${slotDate} ${slotTime}`, 'yyyy-MM-dd HH:mm', new Date());
      return isBefore(slotDateTime, new Date());
    } catch (error) {
      console.error('Error parsing slot date/time:', error);
      return false;
    }
  }, []);

  useEffect(() => {
    if (id) {
      loadAvailability();
      loadUserPhone();
      // PRD §5.9 — `sponsor_detail_viewed`. The agenda page is the
      // canonical "detail" entry today (`app/sponsors/[id].tsx` doesn't
      // exist in the current routing tree); when a dedicated profile
      // screen ships, move the call there and document in the PRD.
      import('@/utils/clarityEvents')
        .then(({ logSponsorDetailViewed }) => logSponsorDetailViewed({ sponsor_id: String(id) }))
        .catch(() => {});
    }
  }, [id]);

  const loadUserPhone = async () => {
    try {
      console.log('📞 Loading phone for user via API...');
      let profile = null;
      try {
        profile = await profileService.getProfile();
      } catch (profileError) {
        console.warn('⚠️ Could not fetch profile for phone:', profileError);
        return;
      }

      console.log('📞 Profile phone data:', profile?.phone);

      if (profile?.phone) {
        console.log('✅ User has phone number:', profile.phone);
        setUserPhone(profile.phone);
      } else {
        console.log('❌ User does NOT have phone number');
      }
    } catch (err) {
      console.error('Error loading user phone:', err);
    }
  };

  const loadAvailability = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getSponsorAvailability(id!);
      console.log('📅 Availability data received:', data);
      setSponsor(data.sponsor);
      const slots = data.slots || [];
      console.log('📅 Slots to set:', slots.length);
      setAllSlots(slots);
      setGroupedSlots(groupSlotsByDate(slots));
    } catch (err) {
      console.error('Error loading availability:', err);
      setError(t('sponsors.agenda.error'));
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = async () => {
    if (!selectedSlot) {
      Alert.alert('Erro', 'Por favor, selecione um horário');
      return;
    }

    // Reschedule mode: call the reschedule endpoint directly — no phone step needed
    // since the user's phone is already stored on the original appointment.
    if (isRescheduling) {
      if (!resolvedAppointmentId) {
        Alert.alert('Erro', 'ID do agendamento inválido');
        return;
      }
      setScheduling(true);
      try {
        await rescheduleAppointment({
          appointmentId: resolvedAppointmentId,
          newDate: selectedSlot.date,
          newTime: selectedSlot.time,
        });
        import('@/utils/clarityEvents')
          .then(({ logSponsorCallRequested }) =>
            logSponsorCallRequested({ sponsor_id: String(id) })
          )
          .catch(() => {});
        router.replace({
          pathname: '/sponsors/success',
          params: {
            sponsorName: sponsor?.name || '',
            isReschedule: '1',
          },
        });
      } catch (err: any) {
        const msg: string = err?.message || '';
        if (err?.code === 'APPOINTMENT_CHANGE_WINDOW_CLOSED') {
          Alert.alert('Erro', t('sponsors.profile.changeWindowClosed'));
        } else if (msg.includes('no longer available') || msg.includes('slot')) {
          Alert.alert('Erro', t('sponsors.profile.rescheduleSlotTaken'));
        } else if (msg.includes('past')) {
          Alert.alert('Erro', t('sponsors.profile.reschedulePastError'));
        } else {
          Alert.alert('Erro', t('sponsors.profile.rescheduleError'));
        }
      } finally {
        setScheduling(false);
      }
      return;
    }

    // Standard booking: fire clarity event, then navigate to phone confirmation.
    // PRD §5.9 — `sponsor_call_requested`. Fired the moment the user
    // confirms a slot — capturing intent, not the (multi-step) phone
    // confirmation outcome. The phone-page handler can refine later if
    // we want a separate "scheduled" event.
    import('@/utils/clarityEvents')
      .then(({ logSponsorCallRequested }) => logSponsorCallRequested({ sponsor_id: String(id) }))
      .catch(() => {});

    console.log('🎯 handleConfirm: navigating to phone page for confirmation');
    console.log('📞 Existing userPhone:', userPhone || 'none');

    // Always navigate to phone page for confirmation
    // Pass existing phone so it can be pre-filled
    router.push({
      pathname: '/sponsors/phone',
      params: {
        sponsorId: id,
        sponsorName: sponsor?.name || '',
        sponsorPhotoUrl: sponsor?.photo_url || '',
        sponsorRole: sponsor?.role || '',
        scheduledDate: selectedSlot.date,
        scheduledTime: selectedSlot.time,
        existingPhone: userPhone || '',
        amountMinor: Number.isInteger(sponsor?.telemed?.amount_minor)
          ? String(sponsor?.telemed?.amount_minor)
          : '',
        currency: sponsor?.telemed?.currency || '',
        sessionDurationMinutes: Number.isInteger(sponsor?.sessionDurationMinutes)
          ? String(sponsor?.sessionDurationMinutes)
          : '',
      },
    });
  };

  const formatDateHeader = (dateStr: string) => {
    const date = parseISO(dateStr);
    return format(date, "dd 'de' MMMM, EEEE", { locale: getLocale() });
  };

  const getDateRangeFormatted = () => {
    if (allSlots.length === 0) return { startDate: '', endDate: '' };
    const firstSlot = allSlots[0];
    const lastSlot = allSlots[allSlots.length - 1];
    if (!firstSlot || !lastSlot) return { startDate: '', endDate: '' };
    const firstDate = parseISO(firstSlot.date);
    const lastDate = parseISO(lastSlot.date);
    return {
      startDate: format(firstDate, 'dd/MM', { locale: getLocale() }),
      endDate: format(lastDate, 'dd/MM', { locale: getLocale() }),
    };
  };

  return (
    <SafeAreaView style={styles.container} edges={['top']}>
      <PageHeader
        title={isRescheduling ? t('sponsors.profile.rescheduleTitle') : t('sponsors.agenda.title')}
        onBack={() => router.back()}
      />

      {/* Sponsor Header */}
      {sponsor && (
        <View style={styles.sponsorHeader}>
          <View style={styles.sponsorAvatarWrapper}>
            <Avatar uri={sponsor.photo_url} size={40} style={{ borderRadius: 12 }} />
          </View>
          <View style={styles.sponsorTextBlock}>
            <Text style={styles.sponsorName}>{sponsor.name}</Text>
            {Number.isInteger(sponsor.sessionDurationMinutes) &&
              sponsor.sessionDurationMinutes > 0 && (
                <Text style={styles.sponsorSessionDuration}>
                  {t('sponsors.details.sessionDuration', {
                    minutes: String(sponsor.sessionDurationMinutes),
                  })}
                </Text>
              )}
          </View>
        </View>
      )}

      <ScrollView style={styles.content} contentContainerStyle={styles.scrollContent}>
        {/* Loading State */}
        {loading && (
          <>
            {/* Date Range Card Skeleton */}
            <View style={styles.dateRangeCard}>
              <Skeleton width={24} height={24} borderRadius={6} />
              <View style={{ flex: 1 }}>
                <Skeleton width='80%' height={14} borderRadius={4} />
              </View>
            </View>

            {/* Date Group Skeletons */}
            {[1, 2, 3].map((index) => (
              <View key={index} style={styles.dateGroup}>
                <Skeleton width={180} height={16} borderRadius={4} style={{ marginBottom: 12 }} />
                <View style={styles.timeSlotsGrid}>
                  {[1, 2, 3, 4, 5, 6, 7, 8].map((slotIndex) => (
                    <View key={slotIndex} style={styles.timeChipSkeleton}>
                      <Skeleton width='100%' height={32} borderRadius={8} />
                    </View>
                  ))}
                </View>
              </View>
            ))}
          </>
        )}

        {/* Error State */}
        {error && !loading && (
          <View style={styles.errorContainer}>
            <Text style={styles.errorText}>{error}</Text>
            <TouchableOpacity onPress={loadAvailability} style={styles.retryButton}>
              <Text style={styles.retryButtonText}>Tentar novamente</Text>
            </TouchableOpacity>
          </View>
        )}

        {/* Content */}
        {!loading && !error && (
          <>
            {/* Date Range Header */}
            <View style={styles.dateRangeCard}>
              <CalendarIcon />
              <Text style={styles.dateRangeText}>
                {t('sponsors.agenda.schedules')}{' '}
                <Text style={styles.dateRangeTextBold}>{getDateRangeFormatted().startDate}</Text>{' '}
                {t('sponsors.agenda.to')}{' '}
                <Text style={styles.dateRangeTextBold}>{getDateRangeFormatted().endDate}</Text>
              </Text>
            </View>

            {sponsor && (
              <View style={styles.agendaDetails}>
                <ProfessionalDetails
                  sponsor={sponsor}
                  labels={professionalDetailLabels}
                  locale={locale}
                  formatSessionDuration={formatSessionDuration}
                  visibleRows={AGENDA_VISIBLE_DETAIL_ROWS}
                />
              </View>
            )}

            {/* Grouped Slots by Date - Filter out days with no available slots */}
            {(() => {
              const daysWithAvailableSlots = Object.entries(groupedSlots).filter(([, slots]) => {
                return slots.some((slot) => slot.available && !isSlotInPast(slot.date, slot.time));
              });

              if (daysWithAvailableSlots.length === 0) {
                return <Text style={styles.emptyText}>{t('sponsors.agenda.noSlots')}</Text>;
              }

              return daysWithAvailableSlots.map(([date, slots]) => (
                <View key={date} style={styles.dayGroup}>
                  <Text style={styles.dayHeader}>{formatDateHeader(date)}</Text>
                  <View style={styles.timeSlotsGrid}>
                    {slots.map((slot, index) => {
                      const isSelected =
                        selectedSlot?.date === slot.date && selectedSlot?.time === slot.time;
                      const isPast = isSlotInPast(slot.date, slot.time);
                      const isAvailable = slot.available && !isPast;

                      return (
                        <TouchableOpacity
                          key={index}
                          style={[
                            styles.timeChip,
                            isSelected && styles.timeChipSelected,
                            !isAvailable && styles.timeChipDisabled,
                          ]}
                          onPress={() =>
                            isAvailable && setSelectedSlot({ date: slot.date, time: slot.time })
                          }
                          disabled={!isAvailable}
                          activeOpacity={isAvailable ? 0.7 : 1}
                        >
                          <Text
                            style={[
                              styles.timeChipText,
                              isSelected && styles.timeChipTextSelected,
                              !isAvailable && styles.timeChipTextDisabled,
                            ]}
                          >
                            {slot.time}
                          </Text>
                        </TouchableOpacity>
                      );
                    })}
                  </View>
                </View>
              ));
            })()}
          </>
        )}
      </ScrollView>

      {/* Bottom Confirmation Section */}
      {!loading && !error && selectedSlot && (
        <View
          style={[
            styles.bottomSection,
            { paddingBottom: keyboardVisible ? 16 : insets.bottom + 24 },
          ]}
        >
          <Text style={styles.selectedTimeText}>
            <Text style={styles.selectedTimeBold}>
              {format(parseISO(selectedSlot.date), "dd 'de' MMMM, EEEE", { locale: getLocale() })}
            </Text>{' '}
            {t('sponsors.profile.at')}{' '}
            <Text style={styles.selectedTimeBold}>{selectedSlot.time}</Text>
          </Text>
          <TouchableOpacity
            style={[styles.confirmButton, scheduling && styles.confirmButtonDisabled]}
            onPress={handleConfirm}
            disabled={scheduling}
            activeOpacity={0.8}
          >
            {scheduling ? (
              <ActivityIndicator size='small' color='#FFFFFF' />
            ) : (
              <>
                <Svg width='24' height='24' viewBox='0 0 24 24' fill='none'>
                  <Path
                    d='M15.1523 3.69238V6.6677'
                    stroke='white'
                    strokeWidth='2'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                  />
                  <Path
                    d='M7.92969 3.69238V6.6677'
                    stroke='white'
                    strokeWidth='2'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                  />
                  <Path
                    fillRule='evenodd'
                    clipRule='evenodd'
                    d='M15.683 5.06445L7.40863 5.06527C4.97812 5.06527 3.46094 6.41874 3.46094 8.90651V16.3957C3.46094 18.9229 4.97812 20.3077 7.40863 20.3077L15.6759 20.3068C18.1136 20.3068 19.6235 18.9462 19.6235 16.4576V8.90569C19.6307 6.41792 18.1207 5.06445 15.683 5.06445Z'
                    stroke='white'
                    strokeWidth='2'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                  />
                  <Path
                    d='M7.52344 11.4375H7.53344'
                    stroke='white'
                    strokeWidth='2'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                  />
                  <Path
                    d='M7.52344 14.8916H7.53344'
                    stroke='white'
                    strokeWidth='2'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                  />
                  <Path
                    d='M11.5391 11.4375H11.5491'
                    stroke='white'
                    strokeWidth='2'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                  />
                  <Path
                    d='M11.5391 14.8916H11.5491'
                    stroke='white'
                    strokeWidth='2'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                  />
                  <Path
                    d='M15.5391 11.4375H15.5491'
                    stroke='white'
                    strokeWidth='2'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                  />
                  <Path
                    d='M15.5391 14.8916H15.5491'
                    stroke='white'
                    strokeWidth='2'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                  />
                </Svg>
                <Text style={styles.confirmButtonText}>
                  {isRescheduling
                    ? t('sponsors.agenda.rescheduleConfirmButton')
                    : t('sponsors.agenda.confirmButton')}
                </Text>
              </>
            )}
          </TouchableOpacity>
        </View>
      )}
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#FFFFFF',
  },
  sponsorHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 24,
    paddingTop: 0,
    paddingBottom: 24,
    gap: 12,
  },
  sponsorAvatarWrapper: {
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#C4C2BA',
    overflow: 'hidden',
  },
  sponsorTextBlock: {
    flex: 1,
    gap: 2,
  },
  sponsorName: {
    fontSize: 20,
    lineHeight: 28,
    fontFamily: 'Sentient-Regular',
    color: '#151515',
  },
  sponsorSessionDuration: {
    fontSize: 12,
    fontFamily: 'Inter_700Bold',
    color: '#669AB7',
    lineHeight: 12,
  },
  content: {
    flex: 1,
  },
  scrollContent: {
    paddingHorizontal: 24,
    paddingTop: 0,
    paddingBottom: 120,
  },
  dateGroup: {
    marginBottom: 24,
  },
  loadingContainer: {
    alignItems: 'center',
    paddingVertical: 40,
  },
  loadingText: {
    marginTop: 12,
    fontSize: 14,
    color: '#757C85',
    fontFamily: 'Inter_400Regular',
  },
  errorContainer: {
    alignItems: 'center',
    paddingVertical: 40,
  },
  errorText: {
    fontSize: 14,
    color: '#757C85',
    textAlign: 'center',
    marginBottom: 16,
    fontFamily: 'Inter_400Regular',
  },
  retryButton: {
    paddingHorizontal: 16,
    paddingVertical: 8,
    backgroundColor: '#015480',
    borderRadius: 8,
  },
  retryButtonText: {
    fontSize: 14,
    color: '#FFFFFF',
    fontFamily: 'Inter_500Medium',
  },
  emptyText: {
    fontSize: 14,
    color: '#757C85',
    textAlign: 'center',
    paddingVertical: 40,
    fontFamily: 'Inter_400Regular',
  },
  dateRangeCard: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 16,
    backgroundColor: '#F9F8F3',
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#F0EEE4',
    marginTop: 0,
    marginBottom: 16,
    gap: 12,
  },
  agendaDetails: {
    padding: 0,
    marginBottom: 24,
  },
  dateRangeText: {
    fontSize: 14,
    color: '#3D3C38',
    fontFamily: 'Inter_400Regular',
    lineHeight: 19.6,
  },
  dateRangeTextBold: {
    fontFamily: 'Inter_700Bold',
  },
  dayGroup: {
    marginBottom: 24,
  },
  dayHeader: {
    fontSize: 14,
    color: '#151515',
    marginBottom: 16,
    fontFamily: 'Inter_700Bold',
    lineHeight: 14,
  },
  timeSlotsGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
    width: '100%',
  },
  timeChip: {
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 17,
    borderWidth: 1,
    borderColor: '#C4C2BA',
    width: '23%',
    alignItems: 'center',
    justifyContent: 'center',
  },
  timeChipSelected: {
    backgroundColor: '#669AB7',
    borderColor: '#669AB7',
  },
  timeChipDisabled: {
    backgroundColor: '#F9F8F3',
    borderColor: '#F9F8F3',
  },
  timeChipText: {
    fontSize: 12,
    color: '#3D3C38',
    fontFamily: 'Inter_500Medium',
    lineHeight: 16.8,
  },
  timeChipTextSelected: {
    color: '#FFFFFF',
  },
  timeChipTextDisabled: {
    color: '#C4C2BA',
  },
  timeChipSkeleton: {
    width: '23%',
  },
  bottomSection: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    backgroundColor: '#FFFFFF',
    paddingHorizontal: 24,
    paddingTop: 24,
    gap: 16,
    borderTopWidth: 1,
    borderTopColor: '#F0EEE4',
  },
  selectedTimeText: {
    fontSize: 14,
    color: '#151515',
    textAlign: 'center',
    fontFamily: 'Inter_400Regular',
    lineHeight: 19.6,
  },
  selectedTimeBold: {
    fontFamily: 'Inter_700Bold',
  },
  confirmButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#151515',
    borderRadius: 12,
    padding: 16,
    gap: 8,
    height: 56,
  },
  confirmButtonDisabled: {
    opacity: 0.6,
  },
  confirmButtonText: {
    fontSize: 14,
    color: '#FFFFFF',
    fontFamily: 'Inter_500Medium',
    lineHeight: 19.6,
    textAlign: 'center',
  },
});
